import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { PluginPreviewProps } from '../../types/PluginInterface';
import { EventBookingConfig, EventTicketCategory, EventCoupon } from './types';
import AutoTranslateText from '../../components/AutoTranslateText';
import { api } from '../../services/api';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import PayPalCheckoutButtons from '../../components/PayPalCheckoutButtons';
import StripePaymentElementForm from '../../components/StripePaymentElementForm';
import { ALL_COUNTRIES } from '../../utils/countries';

interface EnrichedOccurrence {
  id?: string | number;
  startsAt: string;
  endsAt?: string;
  capacity?: number;
  available?: number;
  availableByCategory?: Record<string, number>;
  isBlocked?: boolean;
}

type OccByDate = Record<string, EnrichedOccurrence[]>;

type MerchantKeys = {
  stripePublicKey?: string;
  paypalClientId?: string;
};

// Toutes les clés de date et l'affichage des heures sont en UTC
// pour correspondre exactement aux heures saisies par l'organisateur.
const toLocalDateKey = (date: Date) => {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseDateKey = (key: string) => {
  const parts = key.split('-').map(Number);
  if (parts.length !== 3) return null;
  const [year, month, day] = parts;
  if (!year || !month || !day) return null;
  return new Date(Date.UTC(year, month - 1, day)); // minuit UTC
};

const formatDateLabel = (key: string) => {
  const parsed = parseDateKey(key);
  if (!parsed) return key;
  return parsed.toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC'
  });
};

const normalizeStripeKey = (value?: string | null) => {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  if (trimmed.toLowerCase().startsWith('pk_')) return trimmed;
  const firstPart = trimmed.split(':')[0].trim();
  if (firstPart.toLowerCase().startsWith('pk_')) return firstPart;
  const match = trimmed.match(/pk_(live|test)_[a-z0-9]+/i);
  return match ? match[0] : undefined;
};

const normalizePaypalKey = (value?: string | null) => {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return trimmed.split(':')[0].trim() || undefined;
};

const normalizeCouponCode = (value?: string | null) => {
  if (!value) return '';
  return value.trim().toUpperCase();
};

const resolveCoupon = (coupons: EventCoupon[], rawCode?: string | null) => {
  const code = normalizeCouponCode(rawCode);
  if (!code) return null;
  const match = coupons.find((coupon) => normalizeCouponCode(coupon.code) === code && coupon.active !== false);
  if (!match) return null;
  if (match.maxUses !== undefined && match.maxUses !== null && match.usedCount !== undefined) {
    if (match.usedCount >= match.maxUses) return null;
  }
  return match;
};

const computeDiscountCents = (baseAmount: number, coupon?: EventCoupon | null) => {
  if (!coupon || baseAmount <= 0) return 0;
  if (coupon.type === 'PERCENT') {
    const percent = Math.max(0, Math.min(100, Number(coupon.percentOff || 0)));
    return Math.min(baseAmount, Math.round(baseAmount * (percent / 100)));
  }
  const fixed = Math.max(0, Number(coupon.amountCents || 0));
  return Math.min(baseAmount, fixed);
};

const getVideoEmbedUrl = (rawUrl: string) => {
  try {
    const normalized = /^https?:\/\//i.test(rawUrl) ? rawUrl : `https://${rawUrl}`;
    const url = new URL(normalized);
    const host = url.hostname.toLowerCase();
    if (host.includes('youtube.com')) {
      if (url.pathname.startsWith('/shorts/')) {
        const id = url.pathname.split('/')[2];
        return id ? `https://www.youtube-nocookie.com/embed/${id}` : null;
      }
      const v = url.searchParams.get('v');
      if (v) return `https://www.youtube-nocookie.com/embed/${v}`;
      if (url.pathname.includes('/embed/')) return rawUrl;
    }
    if (host.includes('youtu.be')) {
      const id = url.pathname.replace('/', '');
      return id ? `https://www.youtube-nocookie.com/embed/${id}` : null;
    }
    if (host.includes('vimeo.com')) {
      const id = url.pathname.split('/').filter(Boolean)[0];
      return id ? `https://player.vimeo.com/video/${id}` : null;
    }
  } catch {
    return null;
  }
  return null;
};

const normalizeOwnerId = (value: any) => {
  if (value === undefined || value === null) return '';
  const str = String(value).trim();
  if (!str || str === 'undefined' || str === 'null') return '';
  const num = Number(str);
  if (!Number.isFinite(num) || num <= 0) return '';
  return String(num);
};

const resolveOwnerId = (cfg: any) => {
  const candidates = [
    cfg?.userId,
    cfg?.ownerId,
    cfg?.createdBy,
    cfg?.created_by,
    cfg?.authorId,
    cfg?.user?.id,
    cfg?.user?.userId
  ];
  for (const candidate of candidates) {
    const normalized = normalizeOwnerId(candidate);
    if (normalized) return normalized;
  }
  return '';
};

const CalendarGrid: React.FC<{
  selectedDate?: string;
  selectableDates: Set<string>;
  availableDates: Set<string>;
  primaryColor: string;
  cardBg?: string;
  cardTextColor?: string;
  onSelect: (dateKey: string) => void;
}> = ({ selectedDate, selectableDates, availableDates, primaryColor, cardBg, cardTextColor, onSelect }) => {
  const [cursor, setCursor] = useState(() => {
    if (selectedDate) {
      const parsed = parseDateKey(selectedDate);
      return parsed || new Date();
    }
    return new Date();
  });

  useEffect(() => {
    if (!selectedDate) return;
    const parsed = parseDateKey(selectedDate);
    if (parsed) setCursor(parsed);
  }, [selectedDate]);

  const year = cursor.getUTCFullYear();
  const month = cursor.getUTCMonth();
  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const firstDay = new Date(Date.UTC(year, month, 1)).getUTCDay();
  const startDay = (firstDay + 6) % 7; // Lundi = 0

  const monthNames = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  const blanks = Array.from({ length: startDay });
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const goPrev = () => setCursor(new Date(Date.UTC(year, month - 1, 1)));
  const goNext = () => setCursor(new Date(Date.UTC(year, month + 1, 1)));

  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 14, padding: 16, background: cardBg || '#fff', overflow: 'hidden', minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={goPrev}>
          ◀
        </button>
        <div style={{ fontWeight: 700, color: cardTextColor || '#1e293b' }}>{monthNames[month]} {year}</div>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={goNext}>
          ▶
        </button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 4, fontSize: 12, textAlign: 'center', color: '#64748b', marginBottom: 4 }}>
        {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((d) => (
          <div key={d} style={{ fontWeight: 600 }}>{d}</div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 4 }}>
        {blanks.map((_, i) => (
          <div key={`blank-${i}`} style={{ aspectRatio: '1' }} />
        ))}
        {days.map((day) => {
          const date = new Date(Date.UTC(year, month, day));
          const key = toLocalDateKey(date);
          const isSelectable = selectableDates.has(key);
          const isAvailable = availableDates.has(key);
          const isSelected = selectedDate === key;
          const dayStyle: React.CSSProperties = {
            width: '100%',
            aspectRatio: '1',
            borderRadius: '50%',
            border: !isSelectable
              ? '1px solid #e2e8f0'
              : isAvailable
              ? `2px solid ${primaryColor}`
              : '2px dashed #f59e0b',
            background: isSelected ? primaryColor : '#fff',
            color: isSelected ? '#fff' : isSelectable ? '#1e293b' : '#cbd5e1',
            fontWeight: isSelected ? 700 : 500,
            cursor: isSelectable ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 13,
            padding: 0,
            minWidth: 0,
          };
          return (
            <button
              key={key}
              type="button"
              onClick={() => isSelectable && onSelect(key)}
              disabled={!isSelectable}
              style={dayStyle}
              aria-pressed={isSelected}
              title={isAvailable ? 'Disponible' : isSelectable ? 'Complet (liste d’attente)' : 'Indisponible'}
            >
              {day}
            </button>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 12, justifyContent: 'center', fontSize: 11, color: '#64748b', marginTop: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 12, height: 12, borderRadius: '50%', border: `2px solid ${primaryColor}` }} />
          Disponible
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 12, height: 12, borderRadius: '50%', border: '2px dashed #f59e0b' }} />
          Complet
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 12, height: 12, borderRadius: '50%', border: '1px solid #e2e8f0' }} />
          Indisponible
        </div>
      </div>
    </div>
  );
};

export const EventBookingRenderer: React.FC<PluginPreviewProps> = ({ config }) => {
  const c = config as EventBookingConfig;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [occByDate, setOccByDate] = useState<OccByDate>({});
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const [bookingModal, setBookingModal] = useState<null | { occ: EnrichedOccurrence; mode: 'reservation' | 'waitlist' }>(null);
  const [bookingForm, setBookingForm] = useState({
    qty: '1',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    country: c.country || 'France',
    ticketCategoryId: c.ticketCategories && c.ticketCategories[0]?.id ? String(c.ticketCategories[0].id) : '',
    couponCode: ''
  });
  const [ticketCategories, setTicketCategories] = useState<EventTicketCategory[]>(c.ticketCategories || []);
  const coupons = useMemo(() => (c.coupons || []), [c.coupons]);

  const [paymentStatus, setPaymentStatus] = useState<'idle' | 'success' | 'error' | 'cancelled'>('idle');
  const [paymentMessage, setPaymentMessage] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [confirmedReservation, setConfirmedReservation] = useState<{ reservationId: number; checkInToken: string } | null>(null);

  // Dashboard organisateur
  const [dashTab, setDashTab] = useState<'reservations' | 'stats' | 'mybookings'>('reservations');
  const [dashDate, setDashDate] = useState('');
  const [dashReservations, setDashReservations] = useState<any[]>([]);
  const [dashLoading, setDashLoading] = useState(false);
  const [scanActive, setScanActive] = useState(false);
  const [scanResult, setScanResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const scanIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Mon billet
  const [myEmail, setMyEmail] = useState('');
  const [myBookings, setMyBookings] = useState<any[] | null>(null);
  const [myBookingsLoading, setMyBookingsLoading] = useState(false);
  const [myBookingsError, setMyBookingsError] = useState<string | null>(null);
  const [stripeBusy, setStripeBusy] = useState(false);
  const [stripeClientSecret, setStripeClientSecret] = useState<string | null>(null);
  const [legalModalOpen, setLegalModalOpen] = useState(false);
  const [legalType, setLegalType] = useState<'cgv' | 'rgpd' | null>(null);

  const [merchantKeys, setMerchantKeys] = useState<MerchantKeys>({});
  const [resolvedOwnerId, setResolvedOwnerId] = useState(() => resolveOwnerId(c));
  const normalizedOwnerId = normalizeOwnerId(resolvedOwnerId);

  useEffect(() => {
    setResolvedOwnerId(resolveOwnerId(c));
  }, [
    c.userId,
    c.ownerId,
    (c as any).createdBy,
    (c as any).created_by,
    (c as any).authorId,
    (c as any).user?.id,
    (c as any).user?.userId
  ]);

  const stripePromise = useMemo(
    () => (merchantKeys.stripePublicKey ? loadStripe(merchantKeys.stripePublicKey) : null),
    [merchantKeys.stripePublicKey]
  );
  const stripeElementsOptions = useMemo(() => {
    if (!stripeClientSecret) return undefined;
    return { clientSecret: stripeClientSecret, appearance: { theme: 'stripe' as const } };
  }, [stripeClientSecret]);

  const isConfigured = !!c.slug && !!c.title;
  const primaryColor = c.primaryColor || '#4f46e5';
  const backgroundColor = c.backgroundColor === 'transparent' ? 'transparent' : (c.backgroundColor || '#ffffff');
  const textColor = (c as any).textColor || '#1e293b';

  const getStyle = (prefix: string, defaultColor?: string, defaultSize?: string) => {
    const cfg = c as any;
    return {
      fontFamily: cfg[`${prefix}FontFamily`] !== 'inherit' ? cfg[`${prefix}FontFamily`] : undefined,
      fontSize: cfg[`${prefix}FontSize`] || defaultSize,
      color: cfg[`${prefix}Color`] || defaultColor,
      fontWeight: cfg[`${prefix}Bold`] ? 'bold' : 'normal',
      fontStyle: cfg[`${prefix}Italic`] ? 'italic' : 'normal',
      textDecoration: cfg[`${prefix}Underline`] ? 'underline' : 'none'
    } as React.CSSProperties;
  };

  const titleStyle = getStyle('title', '#1e293b', '1.4rem');
  const descriptionStyle = getStyle('description', '#475569', '0.95rem');

  const actionButtonText = c.actionButtonText || 'Réserver';
  const actionButtonStyle: React.CSSProperties = {
    background: c.actionButtonBackgroundColor || primaryColor,
    color: c.actionButtonTextColor || '#ffffff',
    border: 'none',
    borderRadius: 10,
    padding: '10px 14px',
    cursor: 'pointer',
    fontFamily: c.actionButtonFontFamily && c.actionButtonFontFamily !== 'inherit' ? c.actionButtonFontFamily : undefined,
    fontSize: c.actionButtonFontSize || '0.9rem',
    fontWeight: c.actionButtonBold ? 700 : 600,
    fontStyle: c.actionButtonItalic ? 'italic' : 'normal',
    textDecoration: c.actionButtonUnderline ? 'underline' : 'none'
  };

  const qtyValue = Math.max(1, Number(bookingForm.qty) || 1);
  const selectedCategory = ticketCategories.find((cat) => String(cat.id) === bookingForm.ticketCategoryId) || ticketCategories[0] || null;
  const unitPriceCents = selectedCategory ? selectedCategory.priceCents : (c.priceCents || 0);
  const matchedCoupon = resolveCoupon(coupons, bookingForm.couponCode);
  const couponInvalid = !!bookingForm.couponCode.trim() && !matchedCoupon;
  const baseAmountCents = Math.max(0, qtyValue * unitPriceCents);
  const discountCents = computeDiscountCents(baseAmountCents, matchedCoupon);
  const totalCents = Math.max(0, baseAmountCents - discountCents);

  const loadOccurrences = async () => {
    if (!c.slug || c.slug.length < 3) return;
    setLoading(true);
    setError(null);
    try {
      const data: any = await api.get(`/events/${encodeURIComponent(c.slug)}/available-slots`);
      if (!data?.success || !data?.event) {
        setError('Événement introuvable');
        setOccByDate({});
      } else {
        const categories = Array.isArray(data.event.ticketCategories) ? data.event.ticketCategories : [];
        if (categories.length) {
          setTicketCategories(categories);
        } else if (c.ticketCategories) {
          setTicketCategories(c.ticketCategories);
        }
        const occs: EnrichedOccurrence[] = data.event.occurrences || [];
        const grouped: OccByDate = {};
        occs.forEach((occ) => {
          if (!occ?.startsAt) return;
          const start = new Date(occ.startsAt);
          if (Number.isNaN(start.getTime())) return;
          const key = toLocalDateKey(start);
          const capacity = typeof occ.capacity === 'number' ? occ.capacity : (c.defaultCapacity || 1);
          const available = typeof occ.available === 'number' ? occ.available : capacity;
          const normalized = { ...occ, capacity, available };
          if (!grouped[key]) grouped[key] = [];
          grouped[key].push(normalized);
        });
        Object.keys(grouped).forEach((key) => {
          grouped[key].sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime());
        });
        setOccByDate(grouped);
      }
    } catch (err: any) {
      setError(err?.message || 'Erreur de chargement');
      setOccByDate({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!c.slug || c.slug.length < 3) return;
    const t = setTimeout(() => loadOccurrences(), 600);
    return () => clearTimeout(t);
  // Seulement sur changement de slug : les règles horaires ne se lisent qu'après "Enregistrer l'événement"
  }, [c.slug]);

  useEffect(() => {
    if (c.ticketCategories && c.ticketCategories.length) {
      setTicketCategories(c.ticketCategories);
    }
  }, [c.ticketCategories]);

  useEffect(() => {
    if (!ticketCategories.length) return;
    setBookingForm((prev) => {
      const hasValid = prev.ticketCategoryId && ticketCategories.some((cat) => String(cat.id) === prev.ticketCategoryId);
      if (hasValid) return prev;
      return { ...prev, ticketCategoryId: String(ticketCategories[0].id) };
    });
  }, [ticketCategories]);

  useEffect(() => {
    let cancelled = false;
    const fetchKeys = async () => {
    if (!normalizedOwnerId) {
      setMerchantKeys({});
      return;
    }
    try {
        const keys = (await api.get(`/payment-keys/public/${normalizedOwnerId}`)) as MerchantKeys;
        if (cancelled) return;
        setMerchantKeys({
          stripePublicKey: normalizeStripeKey(keys?.stripePublicKey),
          paypalClientId: normalizePaypalKey(keys?.paypalClientId)
        });
      } catch {
        if (!cancelled) setMerchantKeys({});
      }
    };

    fetchKeys();

    return () => {
      cancelled = true;
    };
  }, [normalizedOwnerId]);

  useEffect(() => {
    if (!c.slug || c.slug.length < 3 || normalizedOwnerId) return;
    let cancelled = false;
    const fetchOwner = async () => {
      try {
        const data: any = await api.get(`/events/${encodeURIComponent(c.slug)}`);
        const owner = data?.event?.ownerId;
        if (!cancelled && owner) {
          setResolvedOwnerId(String(owner));
        }
      } catch {
        // ignore
      }
    };
    const t = setTimeout(fetchOwner, 600);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [c.slug, normalizedOwnerId]);

  const sortedDateKeys = useMemo(() => {
    return Object.keys(occByDate).sort((a, b) => {
      const da = parseDateKey(a);
      const db = parseDateKey(b);
      if (!da || !db) return a.localeCompare(b);
      return da.getTime() - db.getTime();
    });
  }, [occByDate]);

  const selectableDateKeys = useMemo(() => {
    return new Set(
      sortedDateKeys.filter((key) =>
        (occByDate[key] || []).some((occ) => !occ.isBlocked)
      )
    );
  }, [occByDate, sortedDateKeys]);
  const availableDateKeys = useMemo(() => {
    return new Set(
      sortedDateKeys.filter((key) =>
        (occByDate[key] || []).some((occ) => !occ.isBlocked && (occ.available ?? 0) > 0)
      )
    );
  }, [occByDate, sortedDateKeys]);

  useEffect(() => {
    if (selectedDate && occByDate[selectedDate]) return;
    const firstSelectable = sortedDateKeys.find((key) => selectableDateKeys.has(key));
    const firstAvailable = sortedDateKeys.find((key) => availableDateKeys.has(key));
    setSelectedDate(firstAvailable || firstSelectable || null);
  }, [sortedDateKeys, availableDateKeys, selectableDateKeys, occByDate, selectedDate]);

  const selectedOccurrences = selectedDate ? (occByDate[selectedDate] || []) : [];
  const isWaitlistMode = bookingModal?.mode === 'waitlist';
  const selectedAvailable = bookingModal
    ? (bookingModal.occ.availableByCategory && selectedCategory?.id
        ? bookingModal.occ.availableByCategory[String(selectedCategory.id)]
        : bookingModal.occ.available)
    : undefined;

  const openBookingModal = (occ: EnrichedOccurrence, mode: 'reservation' | 'waitlist' = 'reservation') => {
    setBookingForm({
      qty: '1',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      postalCode: '',
      country: c.country || 'France',
      ticketCategoryId: ticketCategories[0]?.id ? String(ticketCategories[0].id) : '',
      couponCode: ''
    });
    setPaymentStatus('idle');
    setPaymentMessage(null);
    setStripeClientSecret(null);
    setBookingModal({ occ, mode });
  };

  const closeBookingModal = () => {
    if (isProcessing) return;
    setStripeClientSecret(null);
    setBookingModal(null);
  };

  const validateForm = () => {
    if (!bookingModal) return null;
    if (!bookingForm.firstName.trim()) return 'Prénom requis';
    if (!bookingForm.lastName.trim()) return 'Nom requis';
    if (!bookingForm.email.trim()) return 'Email requis';
    if (!bookingForm.phone.trim()) return 'Téléphone requis';
    if (!bookingForm.address.trim()) return 'Adresse requise';
    if (!bookingForm.city.trim()) return 'Ville requise';
    if (!bookingForm.postalCode.trim()) return 'Code postal requis';
    if (!bookingForm.country.trim()) return 'Pays requis';
    if (ticketCategories.length && !selectedCategory) return 'Tarif requis';
    const qty = Math.max(1, Number(bookingForm.qty) || 1);
    const availableForCategory = bookingModal.occ.availableByCategory && selectedCategory?.id
      ? bookingModal.occ.availableByCategory[String(selectedCategory.id)]
      : bookingModal.occ.available;
    if (bookingModal.mode !== 'waitlist' && availableForCategory != null && qty > availableForCategory) {
      return 'Nombre de places supérieur à la disponibilité';
    }
    if (couponInvalid) return 'Code promo invalide';
    return null;
  };

  useEffect(() => {
    if (stripeClientSecret) {
      setStripeClientSecret(null);
    }
  }, [bookingForm.qty, bookingForm.couponCode, bookingForm.ticketCategoryId, bookingForm.email, bookingForm.firstName, bookingForm.lastName]);

  const submitWaitlist = async () => {
    if (!bookingModal) return;
    const validationError = validateForm();
    if (validationError) {
      setPaymentStatus('error');
      setPaymentMessage(validationError);
      return;
    }

    const qty = Math.max(1, Number(bookingForm.qty) || 1);
    setIsProcessing(true);
    setPaymentStatus('idle');
    setPaymentMessage(null);

    try {
      const payload = {
        occurrenceStart: bookingModal.occ.startsAt,
        occurrenceEnd: bookingModal.occ.endsAt,
        qty,
        firstName: bookingForm.firstName.trim(),
        lastName: bookingForm.lastName.trim(),
        email: bookingForm.email.trim(),
        phone: bookingForm.phone.trim(),
        address: bookingForm.address.trim(),
        city: bookingForm.city.trim(),
        postalCode: bookingForm.postalCode.trim(),
        country: bookingForm.country.trim(),
        ticketCategoryId: bookingForm.ticketCategoryId || undefined,
        couponCode: bookingForm.couponCode.trim() || undefined,
        returnUrl: window.location.origin + window.location.pathname
      };

      const response: any = await api.post(`/events/${encodeURIComponent(c.slug)}/waitlist`, payload);
      if (!response?.success) {
        const message = response?.message === 'slots_available'
          ? 'Des places sont disponibles. Veuillez réserver.'
          : (response?.message || 'Inscription en liste d’attente impossible');
        setPaymentStatus('error');
        setPaymentMessage(message);
        return;
      }

      setPaymentStatus('success');
      setPaymentMessage('Inscription sur liste d’attente confirmée. Un email a été envoyé.');

      setTimeout(() => {
        closeBookingModal();
        setPaymentStatus('idle');
        setPaymentMessage(null);
      }, 2500);
    } catch (err: any) {
      setPaymentStatus('error');
      setPaymentMessage(err?.message || 'Erreur lors de l’inscription');
    } finally {
      setIsProcessing(false);
    }
  };

  const submitBooking = async (method: 'free' | 'stripe' | 'paypal' | 'cash', transactionId?: string) => {
    if (!bookingModal) return;
    const validationError = validateForm();
    if (validationError) {
      setPaymentStatus('error');
      setPaymentMessage(validationError);
      return;
    }

    const qty = Math.max(1, Number(bookingForm.qty) || 1);
    setIsProcessing(true);
    setPaymentStatus('idle');
    setPaymentMessage(null);

    try {
      const payload = {
        occurrenceStart: bookingModal.occ.startsAt,
        occurrenceEnd: bookingModal.occ.endsAt,
        qty,
        firstName: bookingForm.firstName.trim(),
        lastName: bookingForm.lastName.trim(),
        email: bookingForm.email.trim(),
        phone: bookingForm.phone.trim(),
        address: bookingForm.address.trim(),
        city: bookingForm.city.trim(),
        postalCode: bookingForm.postalCode.trim(),
        country: bookingForm.country.trim(),
        ticketCategoryId: bookingForm.ticketCategoryId || undefined,
        couponCode: bookingForm.couponCode.trim() || undefined,
        paymentMethod: method,
        paymentStatus: method === 'cash' ? 'PENDING' : 'PAID',
        transactionId: transactionId ? String(transactionId) : undefined
      };

      const response: any = await api.post(`/events/${encodeURIComponent(c.slug)}/book`, payload);
      if (!response?.success) {
        const message = response?.message === 'capacity_reached'
          ? 'Créneau complet : plus de place disponible. Veuillez choisir un autre créneau.'
          : (response?.message || 'Réservation impossible');
        setPaymentStatus('error');
        setPaymentMessage(message);
        loadOccurrences();
        return;
      }

      setOccByDate((prev) => {
        const copy = { ...prev };
        const key = toLocalDateKey(new Date(bookingModal.occ.startsAt));
        if (copy[key]) {
          copy[key] = copy[key].map((occ) => {
            if (occ.startsAt !== bookingModal.occ.startsAt) return occ;
            const available = Math.max(0, (occ.available ?? 0) - qty);
            const availableByCategory = occ.availableByCategory ? { ...occ.availableByCategory } : undefined;
            if (availableByCategory && selectedCategory?.id) {
              const catKey = String(selectedCategory.id);
              const prev = availableByCategory[catKey] ?? 0;
              availableByCategory[catKey] = Math.max(0, prev - qty);
            }
            return { ...occ, available, availableByCategory };
          });
        }
        return copy;
      });

      setPaymentStatus('success');
      setPaymentMessage('Réservation confirmée. Un email de confirmation a été envoyé.');

      const resId = response?.reservation?.id ?? response?.reservationId;
      const token = response?.reservation?.checkInToken ?? response?.checkInToken;
      if (resId && token) {
        setConfirmedReservation({ reservationId: Number(resId), checkInToken: String(token) });
      }

      setTimeout(() => {
        closeBookingModal();
        setPaymentStatus('idle');
        setPaymentMessage(null);
        setConfirmedReservation(null);
      }, 30000);
    } catch (err: any) {
      const message = err?.message === 'capacity_reached'
        ? 'Créneau complet : plus de place disponible. Veuillez choisir un autre créneau.'
        : (err?.message || 'Erreur lors de la réservation');
      setPaymentStatus('error');
      setPaymentMessage(message);
      loadOccurrences();
    } finally {
      setIsProcessing(false);
    }
  };

  const createStripeIntent = async () => {
    if (!bookingModal || !c.slug) return;
    if (bookingModal.mode === 'waitlist') return;
    const validationError = validateForm();
    if (validationError) {
      setPaymentStatus('error');
      setPaymentMessage(validationError);
      return;
    }
    if (!resolvedOwnerId) {
      setPaymentStatus('error');
      setPaymentMessage('Propriétaire du plugin non défini.');
      return;
    }

    const qty = Math.max(1, Number(bookingForm.qty) || 1);
    setStripeBusy(true);
    try {
      const payload = {
        ownerId: resolvedOwnerId,
        occurrenceStart: bookingModal.occ.startsAt,
        occurrenceEnd: bookingModal.occ.endsAt,
        qty,
        firstName: bookingForm.firstName.trim(),
        lastName: bookingForm.lastName.trim(),
        email: bookingForm.email.trim(),
        phone: bookingForm.phone.trim(),
        address: bookingForm.address.trim(),
        city: bookingForm.city.trim(),
        postalCode: bookingForm.postalCode.trim(),
        country: bookingForm.country.trim(),
        ticketCategoryId: bookingForm.ticketCategoryId || undefined,
        couponCode: bookingForm.couponCode.trim() || undefined
      };

      const resp: any = await api.post(`/events/${encodeURIComponent(c.slug)}/stripe-intent`, payload);
      if (resp?.free) {
        setPaymentStatus('success');
        setPaymentMessage('Réservation confirmée. Un email de confirmation a été envoyé.');
        loadOccurrences();
        return;
      }
      if (!resp?.clientSecret) {
        setPaymentStatus('error');
        setPaymentMessage(resp?.message || 'Client secret Stripe manquant.');
        return;
      }
      setStripeClientSecret(resp.clientSecret);
    } catch (err: any) {
      const message = err?.message === 'capacity_reached'
        ? 'Créneau complet : plus de place disponible. Veuillez choisir un autre créneau.'
        : (err?.message || 'Erreur Stripe.');
      setPaymentStatus('error');
      setPaymentMessage(message);
      loadOccurrences();
    }
    finally {
      setStripeBusy(false);
    }
  };

  const handleStripePaymentSuccess = async (paymentIntentId: string) => {
    if (!c.slug || !resolvedOwnerId) {
      setPaymentStatus('error');
      setPaymentMessage('Propriétaire du plugin non défini.');
      return;
    }
    setIsProcessing(true);
    try {
      const resp: any = await api.post(`/events/${encodeURIComponent(c.slug)}/stripe-confirm-intent`, {
        ownerId: resolvedOwnerId,
        paymentIntentId
      });
      if (resp?.success) {
        setPaymentStatus('success');
        setPaymentMessage('Paiement confirmé. Votre réservation est validée.');
        loadOccurrences();
        setStripeClientSecret(null);
        const resId = resp?.reservation?.id ?? resp?.reservationId;
        const token = resp?.reservation?.checkInToken ?? resp?.checkInToken;
        if (resId && token) setConfirmedReservation({ reservationId: Number(resId), checkInToken: String(token) });
        setTimeout(() => {
          closeBookingModal();
          setPaymentStatus('idle');
          setPaymentMessage(null);
          setConfirmedReservation(null);
        }, 30000);
      } else {
        setPaymentStatus('error');
        setPaymentMessage(resp?.message || 'Impossible de confirmer le paiement.');
      }
    } catch (err: any) {
      setPaymentStatus('error');
      setPaymentMessage(err?.message || 'Erreur de confirmation Stripe.');
    } finally {
      setIsProcessing(false);
    }
  };

  // ---- Dashboard organisateur (hooks avant early return) ----
  const loadDashReservations = useCallback(async () => {
    if (!c.slug || c.slug.length < 3) return;
    setDashLoading(true);
    try {
      const params = dashDate ? `?date=${dashDate}` : '';
      const data: any = await api.get(`/events/${encodeURIComponent(c.slug)}/reservations${params}`);
      setDashReservations(data?.reservations || []);
    } catch {
      setDashReservations([]);
    } finally {
      setDashLoading(false);
    }
  }, [c.slug, dashDate]);

  useEffect(() => {
    if (dashTab === 'reservations' || dashTab === 'stats') loadDashReservations();
  }, [dashTab, dashDate, loadDashReservations]);

  const handleCheckInByToken = useCallback(async (token: string) => {
    if (!token || !c.slug) return;
    try {
      const resp: any = await api.post(`/events/${encodeURIComponent(c.slug)}/check-in`, { token });
      if (resp?.success) {
        setScanResult({ ok: true, msg: `✅ ${resp.reservation?.name || 'Personne'} — Check-in validé !` });
        loadDashReservations();
      } else {
        setScanResult({ ok: false, msg: `❌ ${resp?.message || 'Token invalide'}` });
      }
    } catch {
      setScanResult({ ok: false, msg: '❌ Erreur réseau' });
    }
    setTimeout(() => setScanResult(null), 4000);
  }, [c.slug, loadDashReservations]);

  const stopScanner = useCallback(() => {
    setScanActive(false);
    if (scanIntervalRef.current) { clearInterval(scanIntervalRef.current); scanIntervalRef.current = null; }
    if (videoRef.current?.srcObject) {
      (videoRef.current.srcObject as MediaStream).getTracks().forEach((t) => t.stop());
      videoRef.current.srcObject = null;
    }
  }, []);

  const startScanner = useCallback(async () => {
    setScanActive(true);
    setScanResult(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      if (!videoRef.current) { stopScanner(); return; }
      videoRef.current.srcObject = stream;
      await videoRef.current.play();
      const jsQR = (await import('jsqr')).default;
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      scanIntervalRef.current = setInterval(() => {
        if (!videoRef.current || !ctx) return;
        canvas.width = videoRef.current.videoWidth;
        canvas.height = videoRef.current.videoHeight;
        ctx.drawImage(videoRef.current, 0, 0);
        const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(img.data, img.width, img.height);
        if (code?.data) {
          const match = code.data.match(/token=([^&]+)/);
          const token = match ? decodeURIComponent(match[1]) : code.data.trim();
          if (token) { stopScanner(); handleCheckInByToken(token); }
        }
      }, 500);
    } catch {
      setScanActive(false);
      setScanResult({ ok: false, msg: '❌ Accès caméra refusé' });
    }
  }, [stopScanner, handleCheckInByToken]);

  const loadMyBookings = useCallback(async () => {
    if (!myEmail.includes('@') || !c.slug) return;
    setMyBookingsLoading(true);
    setMyBookingsError(null);
    try {
      const data: any = await api.get(`/events/${encodeURIComponent(c.slug)}/my-bookings?email=${encodeURIComponent(myEmail)}`);
      setMyBookings(data?.reservations || []);
    } catch {
      setMyBookingsError('Erreur lors de la recherche.');
    } finally {
      setMyBookingsLoading(false);
    }
  }, [c.slug, myEmail]);

  const stats = useMemo(() => {
    const total = dashReservations.reduce((s, r) => s + (r.amountCents || 0), 0);
    const paid = dashReservations.filter((r) => r.status === 'PAID').length;
    const checkedIn = dashReservations.filter((r) => r.checkedInAt).length;
    const byCategory: Record<string, { count: number; revCents: number }> = {};
    for (const r of dashReservations) {
      const cat = r.ticketCategory?.name || 'Standard';
      if (!byCategory[cat]) byCategory[cat] = { count: 0, revCents: 0 };
      byCategory[cat].count += r.qty || 1;
      byCategory[cat].revCents += r.amountCents || 0;
    }
    return { total, paid, checkedIn, byCategory, totalCount: dashReservations.length };
  }, [dashReservations]);

  if (!isConfigured) {
    return (
      <div style={{ border: '2px dashed #cbd5e1', borderRadius: 12, padding: 32, textAlign: 'center', background: '#f8fafc' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>🎟️</div>
        <div style={{ fontWeight: 600, fontSize: 18, color: '#334155', marginBottom: 8 }}>
          <AutoTranslateText sourceLang="fr">Réservation Événement</AutoTranslateText>
        </div>
        <div style={{ color: '#64748b', fontSize: 14 }}>
          <AutoTranslateText sourceLang="fr">Configurez le titre et enregistrez</AutoTranslateText>
        </div>
      </div>
    );
  }

  const displayPriceCents = ticketCategories.length
    ? Math.min(...ticketCategories.map((cat) => cat.priceCents || 0))
    : Number(c.priceCents || 0);
  const currency = c.currency || 'EUR';
  const priceLabel = displayPriceCents > 0
    ? `${(displayPriceCents / 100).toFixed(2)} ${currency}`
    : 'Gratuit';
  const pricePrefix = ticketCategories.length ? 'À partir de ' : '';
  const rawVideoUrl = c.videoUrl?.trim();
  const safeVideoUrl = rawVideoUrl && !/^https?:\/\//i.test(rawVideoUrl)
    ? `https://${rawVideoUrl}`
    : rawVideoUrl;
  const videoEmbedUrl = safeVideoUrl ? getVideoEmbedUrl(safeVideoUrl) : null;
  const addressParts = [c.addressLine1, c.city, c.postalCode, c.country].filter(Boolean);
  const mapEnabled = c.enableGeolocation !== false && addressParts.length > 0;
  const mapQuery = mapEnabled ? encodeURIComponent(addressParts.join(', ')) : '';
  const mapBlock = mapEnabled ? (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 14, overflow: 'hidden', background: '#ffffff' }}>
      <div style={{ padding: '8px 12px', fontWeight: 600, fontSize: 13, color: '#1e293b', borderBottom: '1px solid #e2e8f0' }}>
        Localisation
      </div>
      <iframe
        title="Carte"
        src={`https://www.google.com/maps?q=${mapQuery}&output=embed`}
        style={{ width: '100%', height: 220, border: 0, display: 'block' }}
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
      />
      <div style={{ padding: '8px 12px', textAlign: 'right' }}>
        <a href={`https://www.google.com/maps/search/?api=1&query=${mapQuery}`} target="_blank" rel="noreferrer">
          Ouvrir dans Google Maps ↗
        </a>
      </div>
    </div>
  ) : null;

  const openLegalModal = (type: 'cgv' | 'rgpd') => {
    setLegalType(type);
    setLegalModalOpen(true);
  };

  const closeLegalModal = () => {
    setLegalModalOpen(false);
    setLegalType(null);
  };

  // Déterminer si l'utilisateur courant est le propriétaire (calcul non-hook)
  const currentUserStr = (() => { try { const u = (window as any).__flexihub_user || JSON.parse(localStorage.getItem('user') || '{}'); return String(u?.id || u?.userId || ''); } catch { return ''; } })();
  const isOwner = !!(normalizedOwnerId && currentUserStr && normalizedOwnerId === currentUserStr);

  return (
    <div style={{
      border: '1px solid #e2e8f0',
      borderRadius: 18,
      padding: 20,
      fontFamily: 'system-ui',
      background: backgroundColor,
      color: textColor
    }}>
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: 16 }}>
        {c.imageUrl && (
          <img
            src={c.imageUrl}
            alt={c.title}
            style={{ width: 140, height: 100, objectFit: 'cover', borderRadius: 12, flexShrink: 0 }}
          />
        )}
        <div style={{ flex: 1, minWidth: 220 }}>
          <h3 style={{ margin: '0 0 6px', ...titleStyle }}>{c.title}</h3>
          <div style={{ fontSize: 13, color: '#64748b', fontWeight: 600, marginBottom: 8 }}>
            {pricePrefix}{priceLabel}
          </div>
          {c.description && (
            <div
              style={{ margin: 0, lineHeight: 1.5, ...descriptionStyle }}
              dangerouslySetInnerHTML={{ __html: c.description }}
            />
          )}
        </div>
      </div>

      {safeVideoUrl && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ border: '1px solid #e2e8f0', borderRadius: 14, overflow: 'hidden', background: '#ffffff' }}>
            <div style={{ padding: '8px 12px', fontWeight: 600, fontSize: 13, color: '#1e293b', borderBottom: '1px solid #e2e8f0' }}>
              Vidéo
            </div>
            <div style={{ position: 'relative', width: '100%', paddingTop: '56.25%', background: '#000' }}>
              {videoEmbedUrl ? (
                <iframe
                  title="Vidéo"
                  src={videoEmbedUrl}
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <video
                  controls
                  src={safeVideoUrl}
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
                />
              )}
            </div>
            {!videoEmbedUrl && (
              <div style={{ padding: '8px 12px', fontSize: 12, color: '#64748b' }}>
                <a href={safeVideoUrl} target="_blank" rel="noreferrer">Ouvrir la vidéo ↗</a>
              </div>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="alert alert-danger">{error}</div>
      )}
      {!error && loading && (
        <div className="text-center text-muted" style={{ padding: 20 }}>
          Chargement...
        </div>
      )}
      {!loading && !error && sortedDateKeys.length === 0 && (
        <div className="text-center text-muted" style={{ padding: 20 }}>
          Aucune date disponible
        </div>
      )}
      {!loading && !error && sortedDateKeys.length === 0 && mapBlock && (
        <div style={{ marginTop: 16 }}>
          {mapBlock}
        </div>
      )}

      {!error && sortedDateKeys.length > 0 && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(260px,100%),1fr))', gap: 20, minWidth: 0, overflow: 'hidden' }}>
            <div style={{ minWidth: 0, overflow: 'hidden' }}>
            <CalendarGrid
              selectedDate={selectedDate || undefined}
              selectableDates={selectableDateKeys}
              availableDates={availableDateKeys}
              primaryColor={primaryColor}
              cardBg={(c as any).cardBg}
              cardTextColor={(c as any).cardTextColor}
              onSelect={(key) => setSelectedDate(key)}
            />
            </div>

            <div style={{ border: '1px solid #e2e8f0', borderRadius: 14, padding: 16, background: (c as any).cardBg || '#fff', minHeight: 220, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: textColor }}>
                  {selectedDate ? `Créneaux du ${formatDateLabel(selectedDate)}` : 'Créneaux disponibles'}
                </div>
                <button className="btn btn-sm btn-outline-secondary" onClick={loadOccurrences} disabled={loading} style={{ fontSize: 12 }}>
                  {loading ? '...' : '↻ Recharger'}
                </button>
              </div>

              {selectedOccurrences.length === 0 && (
                <div style={{ textAlign: 'center', color: '#94a3b8', padding: 20 }}>
                  Aucun créneau pour cette date
                </div>
              )}

              <div style={{ display: 'grid', gap: 10, maxHeight: 240, overflowY: 'auto' }}>
                {selectedOccurrences.map((occ) => {
                  const start = new Date(occ.startsAt);
                  const end = occ.endsAt ? new Date(occ.endsAt) : new Date(start.getTime() + 60 * 60000);
                  const timeLabel = `${start.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })} - ${end.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })}`;
                  const available = occ.available ?? 0;
                  const isBlocked = !!occ.isBlocked;
                  const isFull = available <= 0;
                  const disabled = isBlocked;
                  const slotsLabel = isBlocked
                    ? 'Indisponible'
                    : isFull
                    ? 'Complet'
                    : `${available} place${available > 1 ? 's' : ''}`;
                  return (
                    <div
                      key={`${occ.startsAt}-${occ.endsAt || ''}`}
                      style={{
                        border: '1px solid #e2e8f0',
                        borderRadius: 12,
                        padding: 12,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 12,
                        background: disabled ? ((c as any).cardBg || '#f8fafc') : ((c as any).cardBg || '#ffffff')
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 700, color: disabled ? '#94a3b8' : '#1e293b' }}>
                          {timeLabel} · {slotsLabel}
                        </div>
                        {!isBlocked && isFull && (
                          <div style={{ fontSize: 12, color: '#f59e0b' }}>Liste d’attente</div>
                        )}
                      </div>
                      <button
                        type="button"
                        style={{ ...actionButtonStyle, opacity: disabled ? 0.5 : 1 }}
                        disabled={disabled}
                        onClick={() => {
                          if (disabled) return;
                          openBookingModal(occ, isFull ? 'waitlist' : 'reservation');
                        }}
                      >
                        {isBlocked ? 'Indisponible' : isFull ? 'Liste d’attente' : actionButtonText}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          {mapBlock && (
            <div style={{ marginTop: 16 }}>
              {mapBlock}
            </div>
          )}
        </>
      )}

      {bookingModal && createPortal(
        <div style={modalOverlayStyle}>
          <style>{`
            .event-booking-checkout-grid {
              display: grid;
              grid-template-columns: 1fr;
              gap: 1.5rem;
              align-items: start;
            }
            .event-booking-checkout-grid > * { min-width: 0; }
            .event-booking-checkout-payment { position: sticky; top: 0.75rem; align-self: start; }
            @media (min-width: 1200px) { .event-booking-checkout-grid { grid-template-columns: 1fr 1fr; } }
            @media (max-width: 1200px) { .event-booking-checkout-payment { position: static; } }
            @media (max-width: 768px) {
              .event-booking-modal {
                width: 100vw;
                max-height: 100vh;
                border-radius: 0;
                padding: 16px;
              }
            }
          `}</style>
          <div className="event-booking-modal" style={modalCardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#1e293b' }}>
                  {isWaitlistMode ? 'Liste d’attente' : 'Finaliser la réservation'}
                </h3>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                  {new Date(bookingModal.occ.startsAt).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' })}
                  {' · '}
                  {new Date(bookingModal.occ.startsAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })}
                  {' - '}
                  {new Date(bookingModal.occ.endsAt || bookingModal.occ.startsAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })}
                </div>
              </div>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={closeBookingModal}>
                ✕
              </button>
            </div>

            <div className="event-booking-checkout-grid">
              <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, background: '#f8fafc' }}>
                <div style={{ fontWeight: 700, marginBottom: 12, color: '#1e293b' }}>Vos informations</div>
                <div style={{ display: 'grid', gap: 12 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 12 }}>
                    <label style={labelStyle}>
                      Prénom *
                      <input
                        value={bookingForm.firstName}
                        onChange={(e) => setBookingForm((f) => ({ ...f, firstName: e.target.value }))}
                        style={inputStyle}
                      />
                    </label>
                    <label style={labelStyle}>
                      Nom *
                      <input
                        value={bookingForm.lastName}
                        onChange={(e) => setBookingForm((f) => ({ ...f, lastName: e.target.value }))}
                        style={inputStyle}
                      />
                    </label>
                  </div>
                  <label style={labelStyle}>
                    Email *
                    <input
                      type="email"
                      value={bookingForm.email}
                      onChange={(e) => setBookingForm((f) => ({ ...f, email: e.target.value }))}
                      style={inputStyle}
                    />
                  </label>
                  <label style={labelStyle}>
                    Téléphone *
                    <input
                      type="tel"
                      value={bookingForm.phone}
                      onChange={(e) => setBookingForm((f) => ({ ...f, phone: e.target.value }))}
                      style={inputStyle}
                    />
                  </label>
                  <label style={labelStyle}>
                    Adresse *
                    <input
                      value={bookingForm.address}
                      onChange={(e) => setBookingForm((f) => ({ ...f, address: e.target.value }))}
                      style={inputStyle}
                    />
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 12 }}>
                    <label style={labelStyle}>
                      Ville *
                      <input
                        value={bookingForm.city}
                        onChange={(e) => setBookingForm((f) => ({ ...f, city: e.target.value }))}
                        style={inputStyle}
                      />
                    </label>
                    <label style={labelStyle}>
                      Code postal *
                      <input
                        value={bookingForm.postalCode}
                        onChange={(e) => setBookingForm((f) => ({ ...f, postalCode: e.target.value }))}
                        style={inputStyle}
                      />
                    </label>
                    <label style={labelStyle}>
                      Pays *
                      <select
                        value={bookingForm.country}
                        onChange={(e) => setBookingForm((f) => ({ ...f, country: e.target.value }))}
                        style={inputStyle}
                      >
                        {ALL_COUNTRIES.map((c) => (
                          <option key={c.code} value={c.label}>{c.label}</option>
                        ))}
                      </select>
                    </label>
                  </div>
                  {ticketCategories.length > 0 && (
                    <label style={labelStyle}>
                      Tarif *
                      <select
                        value={bookingForm.ticketCategoryId}
                        onChange={(e) => setBookingForm((f) => ({ ...f, ticketCategoryId: e.target.value }))}
                        style={inputStyle}
                      >
                        {ticketCategories.map((cat) => (
                          <option key={String(cat.id)} value={String(cat.id)}>
                            {cat.name} • {(cat.priceCents / 100).toFixed(2)} {currency}
                          </option>
                        ))}
                      </select>
                      {selectedAvailable != null && (
                        <span style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                          Disponibles : {selectedAvailable}
                        </span>
                      )}
                    </label>
                  )}
                  <label style={labelStyle}>
                    Code promo (optionnel)
                    <input
                      value={bookingForm.couponCode}
                      onChange={(e) => setBookingForm((f) => ({ ...f, couponCode: e.target.value }))}
                      style={inputStyle}
                    />
                    {couponInvalid && (
                      <span style={{ fontSize: 12, color: '#ef4444', marginTop: 4 }}>
                        Code promo invalide
                      </span>
                    )}
                  </label>
                  <label style={labelStyle}>
                    Nombre de places *
                    <input
                      type="number"
                      min={1}
                      max={selectedAvailable || undefined}
                      value={bookingForm.qty}
                      onChange={(e) => setBookingForm((f) => ({ ...f, qty: e.target.value }))}
                      style={inputStyle}
                    />
                  </label>
                </div>
              </div>

              <div className="event-booking-checkout-payment" style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: 16 }}>
                <div style={{ fontWeight: 700, marginBottom: 8, color: '#1e293b' }}>Paiement</div>
                <div style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>
                  <div>Total : {(totalCents / 100).toFixed(2)} {currency}</div>
                  {discountCents > 0 && (
                    <div style={{ fontSize: 12 }}>
                      Réduction : -{(discountCents / 100).toFixed(2)} {currency}
                    </div>
                  )}
                </div>

                {paymentStatus === 'error' && paymentMessage && (
                  <div className="alert alert-danger">{paymentMessage}</div>
                )}
                {paymentStatus === 'success' && paymentMessage && (
                  <div>
                    <div className="alert alert-success">{paymentMessage}</div>
                    {confirmedReservation && (
                      <div style={{ textAlign: 'center', marginTop: 16, padding: 16, border: '1px dashed #86efac', borderRadius: 12, background: '#f0fdf4' }}>
                        <div style={{ fontWeight: 700, marginBottom: 10, color: '#166534' }}>Votre billet :</div>
                        <img
                          src={`/api/events/check-in/qr/${encodeURIComponent(confirmedReservation.checkInToken)}`}
                          alt="QR Code"
                          style={{ width: 160, height: 160, border: '1px solid #d1fae5', borderRadius: 8, display: 'block', margin: '0 auto 12px' }}
                        />
                        <a
                          href={`/api/events/ticket/${confirmedReservation.reservationId}/pdf?token=${encodeURIComponent(confirmedReservation.checkInToken)}`}
                          target="_blank"
                          rel="noreferrer"
                          className="btn btn-success btn-sm"
                        >
                          ⬇ Télécharger mon billet (PDF)
                        </a>
                      </div>
                    )}
                  </div>
                )}
                {paymentStatus === 'cancelled' && paymentMessage && (
                  <div className="alert alert-warning">{paymentMessage}</div>
                )}

                {isWaitlistMode ? (
                  <>
                    <div className="alert alert-info">
                      Ce créneau est complet. Vous pouvez rejoindre la liste d’attente.
                    </div>
                    <button
                      type="button"
                      className="btn btn-outline-primary w-100"
                      onClick={submitWaitlist}
                      disabled={isProcessing}
                    >
                      {isProcessing ? '...' : 'Rejoindre la liste d’attente'}
                    </button>
                  </>
                ) : (
                  <>
                    {totalCents <= 0 && (
                      <button
                        type="button"
                        className="btn btn-success w-100"
                        onClick={() => submitBooking('free')}
                        disabled={isProcessing}
                      >
                        {isProcessing ? '...' : 'Confirmer gratuitement'}
                      </button>
                    )}

                    {totalCents > 0 && !resolvedOwnerId && (
                      <div className="alert alert-warning">
                        Paiement indisponible : propriétaire du plugin non défini.
                      </div>
                    )}

                    {totalCents > 0 && resolvedOwnerId && !merchantKeys.stripePublicKey && !merchantKeys.paypalClientId && !c.enableCash && (
                      <div className="alert alert-warning">
                        Aucun moyen de paiement disponible pour cet événement.
                      </div>
                    )}

                    {totalCents > 0 && merchantKeys.stripePublicKey && stripePromise && c.enableStripe !== false && (
                      <div style={{ marginBottom: 16 }}>
                        <div style={{ fontWeight: 600, marginBottom: 8 }}>Paiement par carte (Stripe)</div>
                        {!stripeClientSecret && (
                          <button
                            type="button"
                            className="btn btn-primary w-100"
                            onClick={createStripeIntent}
                            disabled={stripeBusy || isProcessing}
                          >
                            {stripeBusy ? 'Préparation...' : 'Continuer avec Stripe'}
                          </button>
                        )}
                        {stripeClientSecret && stripeElementsOptions && (
                          <Elements stripe={stripePromise} options={stripeElementsOptions}>
                            <StripePaymentElementForm
                              onSuccess={handleStripePaymentSuccess}
                              onError={(err) => {
                                setPaymentStatus('error');
                                setPaymentMessage(String(err || 'Erreur Stripe'));
                              }}
                              submitLabel="Payer par carte"
                              disabled={isProcessing}
                            />
                          </Elements>
                        )}
                      </div>
                    )}

                    {totalCents > 0 && merchantKeys.paypalClientId && c.enablePaypal !== false && (
                      <div>
                        <div style={{ fontWeight: 600, marginBottom: 8 }}>Paiement via PayPal</div>
                        <PayPalScriptProvider options={{ clientId: merchantKeys.paypalClientId, currency }}>
                          <PayPalCheckoutButtons
                            amount={(totalCents / 100).toFixed(2)}
                            currency={currency}
                            onSuccess={(details) => submitBooking('paypal', details?.id)}
                            onError={(err) => {
                              setPaymentStatus('error');
                              setPaymentMessage(String(err || 'Erreur PayPal'));
                            }}
                            onCancel={() => {
                              setPaymentStatus('cancelled');
                              setPaymentMessage('Paiement annulé');
                            }}
                          />
                        </PayPalScriptProvider>
                      </div>
                    )}

                    {totalCents > 0 && c.enableCash && (
                      <div style={{ marginTop: 8 }}>
                        <div style={{ fontWeight: 600, marginBottom: 8 }}>💵 Paiement sur place</div>
                        <button
                          type="button"
                          className="btn btn-outline-secondary w-100"
                          onClick={() => submitBooking('cash')}
                          disabled={isProcessing}
                        >
                          {isProcessing ? 'Traitement...' : 'Réserver et payer sur place'}
                        </button>
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                          Votre réservation sera confirmée — le paiement s'effectue à l'entrée.
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      , document.body)}

      {/* Section "Mon billet" pour les acheteurs */}
      <div style={{ marginTop: 24, border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
        <button
          type="button"
          onClick={() => setDashTab(dashTab === 'mybookings' ? 'reservations' : 'mybookings')}
          style={{ width: '100%', background: '#f8fafc', border: 0, borderBottom: dashTab === 'mybookings' ? '2px solid #4f46e5' : '1px solid #e2e8f0', padding: '10px 16px', textAlign: 'left', fontWeight: 600, fontSize: 14, cursor: 'pointer', color: '#1e293b' }}
        >
          🎫 Retrouver mon billet
        </button>
        {dashTab === 'mybookings' && (
          <div style={{ padding: 16 }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <input
                type="email"
                placeholder="Votre email de réservation"
                value={myEmail}
                onChange={(e) => setMyEmail(e.target.value)}
                style={{ flex: 1, border: '1px solid #cbd5e1', borderRadius: 8, padding: '8px 10px', fontSize: 13 }}
                onKeyDown={(e) => e.key === 'Enter' && loadMyBookings()}
              />
              <button className="btn btn-primary btn-sm" onClick={loadMyBookings} disabled={myBookingsLoading}>
                {myBookingsLoading ? '...' : 'Rechercher'}
              </button>
            </div>
            {myBookingsError && <div className="alert alert-danger">{myBookingsError}</div>}
            {myBookings !== null && myBookings.length === 0 && (
              <div className="alert alert-info">Aucune réservation trouvée pour cet email.</div>
            )}
            {myBookings && myBookings.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {myBookings.map((r: any) => (
                  <div key={r.id} style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 12, background: '#f8fafc' }}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>
                      {r.occurrence?.startsAt ? new Date(r.occurrence.startsAt).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'UTC' }) : 'Date inconnue'}
                      {' '}<span style={{ fontWeight: 400, fontSize: 12, color: '#64748b' }}>× {r.qty}</span>
                    </div>
                    {r.ticketCategory && <div style={{ fontSize: 12, color: '#475569', marginBottom: 4 }}>Tarif : {r.ticketCategory.name}</div>}
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      {r.checkInToken && (
                        <>
                          <img
                            src={`/api/events/check-in/qr/${encodeURIComponent(r.checkInToken)}`}
                            alt="QR"
                            style={{ width: 80, height: 80, border: '1px solid #e2e8f0', borderRadius: 6 }}
                          />
                          <a
                            href={`/api/events/ticket/${r.id}/pdf?token=${encodeURIComponent(r.checkInToken)}`}
                            target="_blank"
                            rel="noreferrer"
                            className="btn btn-outline-success btn-sm"
                          >
                            ⬇ PDF
                          </a>
                        </>
                      )}
                      {r.checkedInAt && <span style={{ fontSize: 12, color: '#059669' }}>✅ Présent</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Dashboard organisateur */}
      {isOwner && (
        <div style={{ marginTop: 24, border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ background: '#f1f5f9', borderBottom: '1px solid #e2e8f0', padding: '10px 16px', fontWeight: 700, fontSize: 15, color: '#1e293b' }}>
            🏠 Dashboard organisateur
          </div>
          <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0' }}>
            {(['reservations', 'stats'] as const).map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setDashTab(tab)}
                style={{ flex: 1, padding: '8px 4px', border: 0, borderBottom: dashTab === tab ? `2px solid ${primaryColor}` : '2px solid transparent', background: 'none', cursor: 'pointer', fontWeight: dashTab === tab ? 700 : 400, fontSize: 13, color: dashTab === tab ? primaryColor : '#64748b' }}
              >
                {tab === 'reservations' ? '📋 Réservations' : '📊 Stats ventes'}
              </button>
            ))}
          </div>

          {/* Filtre date */}
          <div style={{ padding: '10px 16px', borderBottom: '1px solid #e2e8f0', display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="date"
              value={dashDate}
              onChange={(e) => setDashDate(e.target.value)}
              style={{ border: '1px solid #cbd5e1', borderRadius: 8, padding: '4px 8px', fontSize: 13 }}
            />
            <button className="btn btn-sm btn-outline-secondary" onClick={() => setDashDate('')}>Tout</button>
            <button className="btn btn-sm btn-outline-primary" onClick={loadDashReservations} disabled={dashLoading}>
              {dashLoading ? '...' : '↻'}
            </button>
          </div>

          {/* Onglet réservations */}
          {dashTab === 'reservations' && (
            <div style={{ padding: 16 }}>
              {/* Scanner QR */}
              <div style={{ marginBottom: 16 }}>
                {!scanActive ? (
                  <button className="btn btn-outline-primary btn-sm" onClick={startScanner}>
                    📷 Scanner QR code
                  </button>
                ) : (
                  <button className="btn btn-outline-danger btn-sm" onClick={stopScanner}>
                    ⏹ Arrêter le scanner
                  </button>
                )}
                {scanActive && (
                  <div style={{ marginTop: 10, position: 'relative', borderRadius: 10, overflow: 'hidden', border: '2px solid #4f46e5', maxWidth: 320 }}>
                    <video ref={videoRef} style={{ width: '100%', display: 'block' }} muted playsInline />
                    <div style={{ position: 'absolute', inset: 0, border: '2px solid rgba(79,70,229,0.4)', borderRadius: 10, pointerEvents: 'none' }} />
                  </div>
                )}
                {scanResult && (
                  <div className={`alert mt-2 ${scanResult.ok ? 'alert-success' : 'alert-danger'}`}>
                    {scanResult.msg}
                  </div>
                )}
              </div>

              {/* Liste réservations */}
              {dashLoading ? (
                <div style={{ textAlign: 'center', padding: 20, color: '#64748b' }}>Chargement...</div>
              ) : dashReservations.length === 0 ? (
                <div className="alert alert-info">Aucune réservation{dashDate ? ' ce jour' : ''}.</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: '#f8fafc' }}>
                        {['#', 'Nom', 'Email', 'Tarif', 'Qté', 'Montant', 'Statut', 'Check-in', 'Actions'].map((h) => (
                          <th key={h} style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {dashReservations.map((r: any) => (
                        <tr key={r.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '6px 8px', color: '#94a3b8' }}>#{String(r.id).slice(0, 6)}</td>
                          <td style={{ padding: '6px 8px', fontWeight: 600 }}>{r.name}</td>
                          <td style={{ padding: '6px 8px', color: '#475569' }}>{r.email}</td>
                          <td style={{ padding: '6px 8px' }}>{r.ticketCategory?.name || '—'}</td>
                          <td style={{ padding: '6px 8px' }}>{r.qty}</td>
                          <td style={{ padding: '6px 8px' }}>{((r.amountCents || 0) / 100).toFixed(2)} {r.currency || 'EUR'}</td>
                          <td style={{ padding: '6px 8px' }}>
                            <span style={{ display: 'inline-block', borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700, background: r.status === 'PAID' ? '#dcfce7' : '#fef3c7', color: r.status === 'PAID' ? '#166534' : '#92400e' }}>
                              {r.status}
                            </span>
                          </td>
                          <td style={{ padding: '6px 8px' }}>
                            {r.checkedInAt
                              ? <span style={{ color: '#059669', fontWeight: 600 }}>✅ {new Date(r.checkedInAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
                              : <span style={{ color: '#94a3b8' }}>—</span>
                            }
                          </td>
                          <td style={{ padding: '6px 8px' }}>
                            <div style={{ display: 'flex', gap: 4 }}>
                              {!r.checkedInAt && r.checkInToken && (
                                <button
                                  className="btn btn-xs btn-outline-success"
                                  style={{ fontSize: 11, padding: '2px 6px' }}
                                  onClick={() => handleCheckInByToken(r.checkInToken)}
                                >
                                  ✓ Check-in
                                </button>
                              )}
                              {r.checkInToken && (
                                <a
                                  href={`/api/events/ticket/${r.id}/pdf?token=${encodeURIComponent(r.checkInToken)}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="btn btn-xs btn-outline-secondary"
                                  style={{ fontSize: 11, padding: '2px 6px' }}
                                >
                                  PDF
                                </a>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Onglet Stats */}
          {dashTab === 'stats' && (
            <div style={{ padding: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 20 }}>
                {[
                  { label: 'Réservations', value: stats.totalCount, color: '#6366f1' },
                  { label: 'Payées', value: stats.paid, color: '#22c55e' },
                  { label: 'Check-in', value: stats.checkedIn, color: '#f59e0b' },
                  { label: 'Revenu', value: `${(stats.total / 100).toFixed(2)} ${(dashReservations[0]?.currency || 'EUR')}`, color: '#0ea5e9' }
                ].map((card) => (
                  <div key={card.label} style={{ border: `1px solid ${card.color}33`, borderRadius: 10, padding: 12, background: `${card.color}0d` }}>
                    <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>{card.label}</div>
                    <div style={{ fontSize: 20, fontWeight: 800, color: card.color }}>{card.value}</div>
                  </div>
                ))}
              </div>
              <div style={{ fontWeight: 700, marginBottom: 10, fontSize: 14 }}>Par catégorie de billet</div>
              {Object.entries(stats.byCategory).length === 0 ? (
                <div style={{ color: '#94a3b8', fontSize: 13 }}>Aucune donnée.</div>
              ) : (
                Object.entries(stats.byCategory).map(([cat, data]: [string, any]) => {
                  const max = Math.max(...Object.values(stats.byCategory as any).map((d: any) => d.count), 1);
                  return (
                    <div key={cat} style={{ marginBottom: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                        <span style={{ fontWeight: 600 }}>{cat}</span>
                        <span style={{ color: '#475569' }}>{data.count} place(s) — {(data.revCents / 100).toFixed(2)} EUR</span>
                      </div>
                      <div style={{ height: 8, background: '#e2e8f0', borderRadius: 4, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${Math.round((data.count / max) * 100)}%`, background: primaryColor, borderRadius: 4, transition: 'width .3s' }} />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      )}

      {(c.termsCgv || c.termsRgpd) && (
        <div style={{ marginTop: 24, textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 16, flexWrap: 'wrap', fontSize: 13 }}>
            {c.termsCgv && (
              <button type="button" className="btn btn-link p-0" onClick={() => openLegalModal('cgv')}>
                CGU / CGV
              </button>
            )}
            {c.termsRgpd && (
              <button type="button" className="btn btn-link p-0" onClick={() => openLegalModal('rgpd')}>
                RGPD
              </button>
            )}
          </div>
        </div>
      )}

      {legalModalOpen && legalType && createPortal(
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 99999 }} role="dialog" aria-modal="true">
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{legalType === 'cgv' ? 'CGU / CGV' : 'RGPD'}</h5>
                <button type="button" className="btn-close" onClick={closeLegalModal} aria-label="Close" />
              </div>
              <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                <div
                  dangerouslySetInnerHTML={{
                    __html: legalType === 'cgv' ? (c.termsCgv || '') : (c.termsRgpd || '')
                  }}
                />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeLegalModal}>
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
};

const inputStyle: React.CSSProperties = {
  marginTop: 6,
  border: '1px solid #cbd5e1',
  borderRadius: 8,
  padding: '8px 10px',
  fontSize: 13,
  outline: 'none',
  background: '#fff',
  width: '100%'
};

const labelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  fontSize: 13,
  fontWeight: 600,
  color: '#334155'
};

const modalOverlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.55)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
  zIndex: 99999,
  overflowY: 'auto'
};

const modalCardStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: 16,
  width: 'min(920px, 96vw)',
  maxHeight: '92vh',
  overflow: 'auto',
  padding: 24,
  boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
};
